import { h } from 'preact'
import { useMemo } from 'preact/hooks'

import { parseNodeData } from '../../graphql/utils'
import { buildDayPermalink, buildMonthPermalink, buildYearPermalink } from '../../utils/urls'
import BackgroundTitle from '../atom/BackgroundTitle'
import { AsSeparatedList } from '../atom/SeparatedList'
import ArchiveNavigation from '../molecule/ArchiveNavigation'
import ArticleHighlights from '../molecule/article-highlights'
import { AsSiteContentEntry, SiteContentEntry } from '../molecule/SiteContent'
import { formatDateParts } from '../../date/format'
import LinkWrapper from '../atom/link-wrapper'

const NavMenu = AsSiteContentEntry(ArchiveNavigation)

const ArchivePage = (props) => {
  const { year, month, day, rel = {}, entries = [] } = props

  return (
    <>
      <ArchivePageEntry year={year} month={month} day={day} entries={entries} />

      <NavMenu {...rel} withMargin />
    </>
  )
}

const ArchivePageEntryTitle = AsSeparatedList(BackgroundTitle)

export const ArchivePageEntry = ({ year, month, day, entries, smallTitle }) => {
  const titleParts = []

  const formatted = useMemo(
    () => formatDateParts({
      year,
      month,
      day,
    }),
    [year, month, day],
  )

  if (formatted.year) {
    titleParts.push(
      <LinkWrapper href={buildYearPermalink({ year })}>
        {formatted.year}
      </LinkWrapper>,
    )
  }

  if (formatted.month) {
    titleParts.push(
      <LinkWrapper href={buildMonthPermalink({
        year,
        month,
      })}>
        {formatted.month}
      </LinkWrapper>,
    )
  }

  if (formatted.day) {
    titleParts.push(
      <LinkWrapper href={buildDayPermalink({
        year,
        month,
        day,
      })}>
        {formatted.day}
      </LinkWrapper>,
    )
  }

  return (
    <SiteContentEntry>
      <ArchivePageEntryTitle small={smallTitle}>
        {titleParts}
      </ArchivePageEntryTitle>

      <ArticleHighlights entries={entries} />
    </SiteContentEntry>
  )
}

export const MonthGroupedArchiveEntries = ({ entries = [] }) => {
  const grouped = useMemo(
    () => groupByMonth(entries),
    [entries],
  )

  return grouped.map(props => (
    <ArchivePageEntry
      {...props}
      key={`${props.year}_${props.month}`}
    />
  ))
}

function groupByMonth(entries = []) {
  if (!entries) {
    return []
  }

  const mapped = entries.reduce((memo, entry) => {
    const { year, month } = parseNodeData(entry)

    const key = `${year}/${month}`
    const data = memo.get(key) || {
      year,
      month,
      entries: [],
    }

    data.entries.push(entry)
    data.entries.sort(
      (a, b) => b.date.day - a.date.day,
    )

    memo.set(key, data)

    return memo
  }, new Map())

  return Array.from(mapped.values())
    .filter(m => m.entries.length !== 0)
    .sort(
      (a, b) => b.year === a.year ?
        b.month - a.month :
        b.year - a.year,
    )
}

export default ArchivePage
